/**
 * Structured pre-use forms per equipment type (fieldiq-api#1209, migration 0550).
 *
 * Typed to the ACTUAL payloads of `/api/v1/preuse-templates`
 * (src/routes/preuse-templates.routes.ts at api origin/main 337105db): reads are
 * camelCase (the office surface), writes take snake_case keys.
 *
 * The phone's check (`POST /employee/equipment-checks`) is on the `/employee/*`
 * surface and answers snake_case — including the `preuse` block, typed below as
 * `PreuseCheckResult`.
 */

export type PreuseTemplateStatus = 'draft' | 'active' | 'archived';
export const PREUSE_TEMPLATE_STATUSES: readonly PreuseTemplateStatus[] = ['draft', 'active', 'archived'];

export type PreuseTemplateSource = 'manual' | 'starter' | 'ai';

export interface PreuseTemplateItem {
  id: string;
  itemKey: string;
  label: string;
  section: string | null;
  critical: boolean;
  required: boolean;
  helpText: string | null;
  sortOrder: number;
}

/** One row of GET /preuse-templates. */
export interface PreuseTemplate {
  id: string;
  equipmentTypeId: string;
  equipmentTypeName: string | null;
  name: string;
  description: string | null;
  status: PreuseTemplateStatus | string;
  /** Assigned at publish; null on a draft that was never published. */
  version: number | null;
  source: PreuseTemplateSource | string;
  starterKey: string | null;
  starterVersion: number | null;
  formExtractionRunId: string | null;
  itemCount: number;
  criticalCount: number;
  createdBy: string | null;
  createdAt: string | null;
  updatedAt: string | null;
  /** A users.id — the API does not join a name. */
  publishedBy: string | null;
  publishedAt: string | null;
  archivedAt: string | null;
}

/** GET /preuse-templates/:id and every write's response. */
export interface PreuseTemplateDetail extends PreuseTemplate {
  items: PreuseTemplateItem[];
}

/** One entry of GET /preuse-templates/starters (the code catalog). */
export interface PreuseStarter {
  key: string;
  version: number;
  name: string;
  suggestedEquipmentType: string;
  description: string;
  itemCount: number;
  criticalCount: number;
  sections: string[];
  items: Array<Omit<PreuseTemplateItem, 'id'>>;
}

/** GET /preuse-templates/for-equipment/:equipmentId */
export interface PreuseForEquipment {
  equipmentId: string;
  equipmentTypeId: string | null;
  /** The ACTIVE template for the unit's type, or null (every casing unit today). */
  template: PreuseTemplateDetail | null;
}

/** An item as the editor writes it — snake_case, the API's write shape. */
export interface PreuseItemWrite {
  item_key?: string;
  label: string;
  section: string | null;
  critical: boolean;
  required: boolean;
  help_text: string | null;
  sort_order?: number;
}

export type PreuseResult = 'pass' | 'fail' | 'na';
export type PreuseDerivedStatus = 'pass' | 'needs_repair' | 'fail';

/** One answer the phone sends in `checklistItems`. No `status` travels with the check. */
export interface PreuseAnswerWrite {
  item_key: string;
  result: PreuseResult;
  note?: string;
}

/**
 * The `preuse` block on a templated check's 201 (snake_case — /employee surface).
 * An API follow-up may add a field describing a maintenance item a non-critical
 * failure opens; nothing here assumes what that field is called.
 */
export interface PreuseCheckResult {
  template_id: string;
  template_version: number;
  derived_status: PreuseDerivedStatus | string;
  fail_count: number;
  critical_fail_count: number;
  /** api#1211 (may not be deployed): the maintenance item a failed check opened. Absent = unknown. */
  maintenance_item?: PreuseMaintenanceItem | null;
  /** api#1211 (may not be deployed): whether the check moved the unit's status. Absent = unknown. */
  unit_status_changed?: boolean;
  [extra: string]: unknown;
}

export interface PreuseMaintenanceItem {
  id: string;
  status: string | null;
  service_date: string | null;
  description: string | null;
}
